import { service_tourService } from './tours.service';

export type NotificationAction = 'seen' | 'dismissed' | 'ctaClicked' | 'ctaSkipped' | 'seenOnList';

export interface QueuedUpdate {
	notificationId: string;
	action: NotificationAction;
	timestamp: number;
	tourId: string;
}

const QUEUE_KEY_PREFIX = 'notification_queue_';
const ALL_TOUR_QUEUES_KEY = 'notification_queue_tours';

/**
 * Get the localStorage key for a specific tour's notification queue
 */
function getQueueKey(tourId: string): string {
	return `${QUEUE_KEY_PREFIX}${tourId}`;
}

/**
 * Track which tours have pending queues
 */
function addTourToQueueList(tourId: string): void {
	const tours = getTourQueueList();
	if (!tours.includes(tourId)) {
		tours.push(tourId);
		localStorage.setItem(ALL_TOUR_QUEUES_KEY, JSON.stringify(tours));
	}
}

/**
 * Remove a tour from the queue list
 */
function removeTourFromQueueList(tourId: string): void {
	const tours = getTourQueueList().filter((id) => id !== tourId);
	localStorage.setItem(ALL_TOUR_QUEUES_KEY, JSON.stringify(tours));
}

/**
 * Get list of all tours with pending queues
 */
function getTourQueueList(): string[] {
	try {
		const data = localStorage.getItem(ALL_TOUR_QUEUES_KEY);
		return data ? JSON.parse(data) : [];
	} catch {
		return [];
	}
}

/**
 * Add a notification state update to the offline queue
 */
export function queueNotificationUpdate(
	tourId: string,
	notificationId: string,
	action: NotificationAction
): void {
	const key = getQueueKey(tourId);
	const queue = getPendingUpdates(tourId);

	// Check if we already have this notification+action in queue (deduplicate)
	const existingIndex = queue.findIndex(
		(u) => u.notificationId === notificationId && u.action === action
	);

	if (existingIndex === -1) {
		// Add new update to queue
		queue.push({
			notificationId,
			action,
			timestamp: Date.now(),
			tourId,
		});
	} else {
		// Update timestamp of existing entry
		queue[existingIndex].timestamp = Date.now();
	}

	localStorage.setItem(key, JSON.stringify(queue));
	addTourToQueueList(tourId);
}


export function getPendingUpdates(tourId: string): QueuedUpdate[] {
	try {
		const key = getQueueKey(tourId);
		const data = localStorage.getItem(key);
		return data ? JSON.parse(data) : [];
	} catch {
		return [];
	}
}

/**
 * Get all pending updates across all tours
 */
export function getAllPendingUpdates(): Map<string, QueuedUpdate[]> {
	const result = new Map<string, QueuedUpdate[]>();
	const tours = getTourQueueList();

	for (const tourId of tours) {
		const updates = getPendingUpdates(tourId);
		if (updates.length > 0) {
			result.set(tourId, updates);
		}
	}

	return result;
}

/**
 * Clear the queue for a specific tour after successful sync
 */
export function clearQueue(tourId: string): void {
	const key = getQueueKey(tourId);
	localStorage.removeItem(key);
	removeTourFromQueueList(tourId);
}

export async function syncPendingUpdates(tourId: string, uID: string): Promise<boolean> {
	const updates = getPendingUpdates(tourId);

	if (updates.length === 0) {
		return true;
	}
	try {
		const result = await service_tourService.batchUpdateNotificationState(
			tourId,
			uID,
			updates.map((u) => ({
				notificationId: u.notificationId,
				action: u.action,
				timestamp: u.timestamp,
			}))
		);

		if (result.success) {
			clearQueue(tourId);
			return true;
		} else {
			console.warn('[notification-offline] Failed to sync updates for tour:', tourId);
			return false;
		}
	} catch (error) {
		console.error('[notification-offline] Error syncing updates for tour:', tourId, error);
		return false;
	}
}

/**
 * Sync all pending updates across all tours
 */
export async function syncAllPendingUpdates(uID: string): Promise<void> {
	const allUpdates = getAllPendingUpdates();

	if (allUpdates.size === 0) {
		return;
	}
	const syncPromises: Promise<boolean>[] = [];

	for (const [tourId] of allUpdates) {
		syncPromises.push(syncPendingUpdates(tourId, uID));
	}

	const results = await Promise.allSettled(syncPromises);
	const successful = results.filter((r) => r.status === 'fulfilled' && r.value).length;
	const failed = results.length - successful;
}

/**
 * Check if there are any pending updates
 */
export function hasPendingUpdates(): boolean {
	const tours = getTourQueueList();
	for (const tourId of tours) {
		if (getPendingUpdates(tourId).length > 0) {
			return true;
		}
	}
	return false;
}
